import type { Result } from "@pdfking/errors"
import { Failure, Success } from "@pdfking/errors"
import { ToNumber } from "@pdfking/math"
import { UInt8ToString } from "@pdfking/tfunc"

import type { RuntimeBridge } from "#public"
import type { WasmAllocated } from "#public"

// Shared WASM wire-format codec for autoexport's TypeScriptEncoder: the
// Writer, argument/return marshaling, and read-side Cursor every generated
// `src/generated/Encoders.ts` file drove inline before. Centralizing it here
// means a generated file only ever contains the schema-specific encode/decode
// functions and the `CreateFunctions(bridge)` wrapper, not this fixed
// boilerplate — see autoexport's `driver/encoder_typescript.go`.
//
// Every helper below takes its `RuntimeBridge` explicitly rather than closing
// over one, since a module-level export can't close over a per-call value the
// way the old inlined-per-file closures did.

// Writer is the encode side of the wire: every generated `enc_*` appends to
// one growable buffer, so an argument is a single allocation and a single
// write into the instance however many values it carries. Integers below 64
// bits take a bigint where the schema says so, as the cursor returns them.
export type Writer = {
  i8(v: bigint): void
  u8(v: bigint): void
  i16(v: bigint): void
  u16(v: bigint): void
  i32(v: bigint): void
  u32(v: number): void
  f32(v: number): void
  f64(v: number): void
  i64(v: bigint): void
  u64(v: bigint): void
  i128(v: bigint): void
  u128(v: bigint): void
  i256(v: bigint): void
  u256(v: bigint): void
  ptr(v: number | bigint): void
  bool(v: boolean): void
  str(s: string): void
  done(): Uint8Array
}

const encoder = new TextEncoder()

export const CreateWriter = <T extends number | bigint>({ memory64 }: RuntimeBridge<T>): Writer => {
  let u8 = new Uint8Array(256)
  let dv = new DataView(u8.buffer)
  let pos = 0
  const need = (n: number) => {
    if (pos + n <= u8.length) return
    let size = u8.length * 2
    while (size < pos + n) size *= 2
    const next = new Uint8Array(size)
    next.set(u8.subarray(0, pos))
    u8 = next
    dv = new DataView(u8.buffer)
  }
  const u32 = (v: number) => {
    need(4)
    dv.setUint32(pos, v, true)
    pos += 4
  }
  const u64 = (v: bigint) => {
    need(8)
    dv.setBigUint64(pos, BigInt.asUintN(64, v), true)
    pos += 8
  }
  // Four 64-bit limbs, low first, two's complement for the signed ones.
  const limbs = (v: bigint, count: number) => {
    for (let j = 0; j < count; j++) u64(v >> BigInt(j * 64))
  }
  return {
    i8: v => {
      need(1)
      dv.setInt8(pos, ToNumber(BigInt.asIntN(8, v)))
      pos += 1
    },
    u8: v => {
      need(1)
      dv.setUint8(pos, ToNumber(BigInt.asUintN(8, v)))
      pos += 1
    },
    i16: v => {
      need(2)
      dv.setInt16(pos, ToNumber(v), true)
      pos += 2
    },
    u16: v => {
      need(2)
      dv.setUint16(pos, ToNumber(v), true)
      pos += 2
    },
    i32: v => {
      need(4)
      dv.setInt32(pos, ToNumber(v), true)
      pos += 4
    },
    u32,
    f32: v => {
      need(4)
      dv.setFloat32(pos, v, true)
      pos += 4
    },
    f64: v => {
      need(8)
      dv.setFloat64(pos, v, true)
      pos += 8
    },
    i64: u64,
    u64,
    i128: v => limbs(v, 2),
    u128: v => limbs(v, 2),
    i256: v => limbs(v, 4),
    u256: v => limbs(v, 4),
    ptr: v => (memory64 ? u64(BigInt(v)) : u32(ToNumber(v))),
    bool: v => u32(v ? 1 : 0),
    // Length, then the UTF-8 bytes zero-padded to 4.
    str: s => {
      need(7 + 3 * s.length)
      const { written } = encoder.encodeInto(s, u8.subarray(pos + 4))
      dv.setUint32(pos, written, true)
      const padded = (written + 3) & ~3
      u8.fill(0, pos + 4 + written, pos + 4 + padded)
      pos += 4 + padded
    },
    done: () => u8.subarray(0, pos),
  }
}

export type Cursor = {
  i8(): bigint
  u8(): bigint
  i16(): bigint
  u16(): bigint
  i32(): bigint
  u32(): number
  f32(): number
  f64(): number
  i64(): bigint
  u64(): bigint
  i128(): bigint
  u128(): bigint
  i256(): bigint
  u256(): bigint
  ptr(): number
  bool(): boolean
  str(): string
  bytes(): Uint8Array
}

// pa converts a raw pointer/length number to the WASM instance's native
// integer representation (number or bigint, memory32 vs memory64).
export const pa = <T extends number | bigint>(i: RuntimeBridge<T>, v: number): T => i.conv(v)

// write_buf allocates and writes one raw byte buffer (a string or Uint8Array
// argument), returning its [ptr, length].
export const write_buf = <T extends number | bigint>(
  i: RuntimeBridge<T>,
  data: Uint8Array
): [number, number] => {
  const ptr = i.alloc(data.length)
  i.write(ptr, data)
  return [ptr, data.length]
}

// word is the instance's pointer width: the out slot holds two of them.
const word = <T extends number | bigint>(i: RuntimeBridge<T>): number => (i.memory64 ? 8 : 4)

// read_pair reads the [ptr, length] Rust wrote to an out slot.
const read_pair = <T extends number | bigint>(i: RuntimeBridge<T>, p: number): [number, number] => {
  const raw = i.read(p, 2 * word(i))
  const dv = new DataView(raw.buffer, raw.byteOffset, raw.byteLength)
  return i.memory64
    ? [ToNumber(dv.getBigUint64(0, true)), ToNumber(dv.getBigUint64(8, true))]
    : [dv.getUint32(0, true), dv.getUint32(4, true)]
}

// write_pair fills an out slot Rust handed a host callback.
const write_pair = <T extends number | bigint>(
  i: RuntimeBridge<T>,
  p: number,
  [ptr, len]: [number, number]
): void => {
  const raw = new Uint8Array(2 * word(i))
  const dv = new DataView(raw.buffer)
  if (i.memory64) {
    dv.setBigUint64(0, BigInt(ptr), true)
    dv.setBigUint64(8, BigInt(len), true)
  } else {
    dv.setUint32(0, ptr, true)
    dv.setUint32(4, len, true)
  }
  i.write(p, raw)
}

// call_args writes the encoded arguments with the out slot after them in
// one buffer, calls fn, frees the buffer, and returns the [ptr, length] fn
// wrote to the slot.
const call_args = <T extends number | bigint>(
  i: RuntimeBridge<T>,
  fn: string,
  w: Writer
): [number, number] => {
  const args = w.done()
  const total = args.length + 2 * word(i)
  const base = i.alloc(total)
  i.write(base, args)
  const out = base + args.length
  i.call(fn, pa(i, base), pa(i, args.length), pa(i, out))
  const pair = read_pair(i, out)
  i.free(base, total)
  return pair
}

// decode_ret decodes the callee's [ptr, length] return buffer and frees it.
const decode_ret = <T extends number | bigint, R>(
  i: RuntimeBridge<T>,
  [ptr, len]: [number, number],
  decode: (c: Cursor) => R
): R => {
  const result = decode(ReadCursor(i, ptr, len))
  i.free(ptr, len)
  return result
}

// call_with is a generated function call: the arguments `w` encoded, the
// return buffer decoded.
export const call_with = <T extends number | bigint, R>(
  i: RuntimeBridge<T>,
  fn: string,
  w: Writer,
  decode: (c: Cursor) => R
): R => decode_ret(i, call_args(i, fn, w), decode)

// call_void is a generated call of a function returning nothing.
export const call_void = <T extends number | bigint>(
  i: RuntimeBridge<T>,
  fn: string,
  w: Writer
): void => {
  call_args(i, fn, w)
}

// call_start starts a native generator, returning its handle; call_next
// steps it through `next` until it reports done (a non-zero status).
export const call_start = <T extends number | bigint>(
  i: RuntimeBridge<T>,
  fn: string,
  w: Writer
): number => call_args(i, fn, w)[0]

export function* call_next<T extends number | bigint, R>(
  i: RuntimeBridge<T>,
  next: string,
  handle: number,
  decode: (c: Cursor) => R
): Generator<R> {
  const size = 2 * word(i)
  const out = i.alloc(size)
  try {
    while (true) {
      i.call(next, pa(i, handle), pa(i, out))
      const [ptr, len] = read_pair(i, out)
      const c = ReadCursor(i, ptr, len)
      const done = c.u32() !== 0
      const value = done ? undefined : decode(c)
      i.free(ptr, len)
      if (done) return
      yield value as R
    }
  } finally {
    i.free(out, size)
  }
}

// gen_cb registers a host callback Rust invokes once per generator step,
// writing each `gen.next()` result (or a "done" Failure), encoded with enc,
// to the out slot Rust passes.
export const gen_cb = <T extends number | bigint, GT>(
  i: RuntimeBridge<T>,
  gen: Generator<GT>,
  enc: (v: Result<GT>, w: Writer) => void
): WasmAllocated<T> =>
  i.addCallback(
    out => {
      const { done, value } = gen.next()
      const w = CreateWriter(i)
      enc(done ? Failure("done") : Success(value), w)
      write_pair(i, ToNumber(out), write_buf(i, w.done()))
      return 0
    },
    i.memory64 ? "vj" : "vi"
  )

// ReadCursor wraps a [ptr, size] return buffer for sequential scalar reads,
// freeing the buffer is the caller's responsibility (matching every generated
// call site, which frees immediately after decoding).
export const ReadCursor = <T extends number | bigint>(
  i: RuntimeBridge<T>,
  p: number,
  size: number
): Cursor => {
  const raw = i.read(p, size)
  const dv = new DataView(raw.buffer, raw.byteOffset, raw.byteLength)
  let pos = 0
  const i8 = () => {
    const v = dv.getInt8(pos)
    pos += 1
    return BigInt(v)
  }
  const u8r = () => {
    const v = dv.getUint8(pos)
    pos += 1
    return BigInt(v)
  }
  const i16 = () => {
    const v = dv.getInt16(pos, true)
    pos += 2
    return BigInt(v)
  }
  const u16 = () => {
    const v = dv.getUint16(pos, true)
    pos += 2
    return BigInt(v)
  }
  const i32 = () => {
    const v = dv.getInt32(pos, true)
    pos += 4
    return BigInt(v)
  }
  const u32 = () => {
    const v = dv.getUint32(pos, true)
    pos += 4
    return v
  }
  const f32 = () => {
    const v = dv.getFloat32(pos, true)
    pos += 4
    return v
  }
  const f64 = () => {
    const v = dv.getFloat64(pos, true)
    pos += 8
    return v
  }
  const i64 = () => {
    const v = dv.getBigInt64(pos, true)
    pos += 8
    return v
  }
  const u64 = () => {
    const v = dv.getBigUint64(pos, true)
    pos += 8
    return v
  }
  const i128 = () => {
    const lo = dv.getBigUint64(pos, true)
    const hi = dv.getBigInt64(pos + 8, true)
    pos += 16
    return (hi << 64n) | lo
  }
  const u128 = () => {
    const lo = dv.getBigUint64(pos, true)
    const hi = dv.getBigUint64(pos + 8, true)
    pos += 16
    return (hi << 64n) | lo
  }
  const i256 = () => {
    let v = 0n
    for (let j = 0; j < 4; j++) {
      v |= dv.getBigUint64(pos + j * 8, true) << BigInt(j * 64)
    }
    pos += 32
    return v >= 1n << 255n ? v - (1n << 256n) : v
  }
  const u256 = () => {
    let v = 0n
    for (let j = 0; j < 4; j++) {
      v |= dv.getBigUint64(pos + j * 8, true) << BigInt(j * 64)
    }
    pos += 32
    return v
  }
  const ptr = () => {
    if (i.memory64) {
      const v = ToNumber(dv.getBigUint64(pos, true))
      pos += 8
      return v
    }
    return u32()
  }
  const bool = () => !!u32()
  // Length, then the UTF-8 bytes zero-padded to 4, inline in the buffer
  const str = () => {
    const len = u32()
    const s = UInt8ToString(raw.subarray(pos, pos + len))
    pos += (len + 3) & ~3
    return s
  }
  // an empty buffer Rust hands over was never allocated: nothing to free
  const bytes = () => {
    const p = ptr()
    const len = ptr()
    const d = new Uint8Array(i.read(p, len))
    if (len) i.free(p, len)
    return d
  }
  return {
    i8,
    u8: u8r,
    i16,
    u16,
    i32,
    u32,
    f32,
    f64,
    i64,
    u64,
    i128,
    u128,
    i256,
    u256,
    ptr,
    bool,
    str,
    bytes,
  }
}
